import { useState, useEffect } from 'preact/hooks'
import { guestCheckoutAllowed } from '../core/guest-eligibility'
import { apiClient, authStore, handoffUrl, InitData, navigerUd, returMaal, tenantSiteUrl, User } from '../core'
import { t, intlLocale, formatMoney, plural } from '../core/i18n'
import { fmtDag, fmtTid, studietsTidszone } from '../core/tidszone'
import { membersOnlyAccess, paysDropIn } from '../core/members-only'
import { daekningEfterValg, egenPladsBetalerDropIn, kanFravaelgeKlip, klipPaaHoldet, type WidgetCoverage } from '../core/coverage'
import { legalDocumentUrl } from '../core/legal-links'
import { LoginWidget } from '../widgets/login/LoginWidget'
import { PurchaseModal } from './PurchaseModal'
import { ClassPlanChoice, classPlanChoiceStyles, parseCoveringPlans, type CoveringPlan } from './ClassPlanChoice'
import { PaymentMethodPicker, hasPaymentChoice, type InlinePaymentMethod } from './PaymentMethodPicker'
import { CheckoutLegal, checkoutConsent, initialCheckoutLegalState, type CheckoutLegalState } from './CheckoutLegal'

export interface BookableClass {
  id: string
  name: string
  startTime: string
  endTime: string
  instructorName?: string | null
  room?: string | null
  description?: string | null
  maxParticipants?: number | null
  currentParticipants?: number
  availableSpots?: number
  dropInPriceDkk?: number
  waitlistEnabled?: boolean
  membershipRequired?: boolean
  excludeFromMembership?: boolean
  /** «Gratis hold» (OPG-0098): ingen betaling og aldrig et klip; klip-prisen vises ikke. */
  isFree?: boolean
  /** Antal klip holdet koster pr. booking (default 1). Vises når > 1, så et
   *  2-klip-hold ikke ligner et 1-klip når medlemmet bekræfter. */
  clipCost?: number
  /** Holdets SÆRLIGE afmeldingsfrist i timer, kun når den afviger fra studiets
   *  generelle (0 = ingen refundering). Fremhæves, så kunden ser den før booking. */
  ownCancellationHours?: number | null
  classTemplateId?: string | null
  /** Tillader holdet gæster? (fra skabelonen). Inline gæste-stepper når det flade
   *  flow er aktivt; ellers handoff til det native flow. */
  guestsAllowed?: boolean
  /** Maks antal gæster pr. booking (fra skabelonen; 0/undefined = ingen stepper). */
  maxGuests?: number
  /** "Book hele holdet": sand kun når holdtypen tillader det OG holdet er tomt.
   *  Widgeten kan ikke tage betaling inline, så knappen sender kunden til det
   *  native flow (samme handoff-mønster som priced drop-in). */
  fullVenueAvailable?: boolean
  fullVenuePriceDkk?: number | null
  isBooked?: boolean
}

interface Props {
  klass: BookableClass
  initData: InitData
  onClose: () => void
  onBooked?: (result: { confirmedCount: number; waitlistCount: number }) => void
  /** "Kun gæster"-tilstand (widget-attribut guests-only="true"): modalen er
   *  gæste-fokuseret i stedet for self-booking-fokuseret — stepperen starter på 1
   *  gæst og kan ikke gå til 0, og "book dig selv"-rammen fjernes. Medlemmet deltager
   *  stadig (tager en plads, gratis via medlemskab); vi spørger dem bare ikke "vil du
   *  med" — det antages. Bruges på en dedikeret gæste-adgangsside (fx Halgodt). Ren
   *  UI-tilstand: serveren booker medlem + gæster som altid. */
  guestsOnly?: boolean
}

// Holdets dag og tid i studiets tidszone, ikke den besøgendes browser (QA 27-10).
function fmtDay(iso: string, locale: string, tz: string): string {
  return fmtDag(iso, locale, tz, { weekday: 'long', day: 'numeric', month: 'long' })
}
function fmtTime(iso: string, locale: string, tz: string): string {
  return fmtTid(iso, locale, tz)
}
interface BookingResponse {
  success?: boolean
  confirmedCount?: number
  waitlistCount?: number
  usedMembership?: boolean
  // Inline drop-in-betaling: serveren oprettede en pending-booking + Stripe-session
  // og beder widgeten redirecte til den hosted checkout.
  requiresPayment?: boolean
  redirectUrl?: string
}

/**
 * Gæste-fejlkoder hvor serveren svarer med den SAMME lokaliserede tekst som den
 * native gæste-rute (fælles nøgler i lib/bookings/guest-session-errors.ts). Dem
 * viser vi direkte, så fx et forkert telefonnummer siger netop det. To koder har
 * widgeten sin egen tekst til; ukendte koder får den generiske. En jest-test
 * holder listen ens med serverens koder.
 */
export const GUEST_SERVER_TEXT_CODES = [
  'NO_CLASSES', 'MISSING_FIELDS', 'INVALID_EMAIL', 'INVALID_PHONE', 'PHONE_NEEDS_COUNTRY',
  'TERMS_REQUIRED', 'CLASS_NOT_FOUND', 'CLASS_NOT_BOOKABLE', 'SEASON_NOT_ALLOWED',
  'MEMBERS_ONLY', 'FREE_NEEDS_LOGIN', 'CLASS_FULL',
  // OPG-0097: studiet har kun gæstebooking af gratis hold slået til, og holdet er betalt.
  'DISABLED',
]

/** Svar fra /api/widget/guest-session — samme token-form som login/register. */
interface GuestSessionResponse {
  accessToken: string
  refreshToken: string
  expiresIn: number
  user: User
}

/**
 * BookingModal — inline booking confirmation modal for embedded widgets.
 *
 * Mirrors the CMS BookingModal flow but stripped to essentials:
 *   - Logged-out: "Log ind" CTA → tenant /auth/login with returnUrl
 *   - Logged-in: shows class details + price/membership status, single
 *     "Bekræft booking" button that POSTs /api/widget/bookings
 *   - Class full + waitlist enabled: button label becomes "Tilmeld venteliste"
 *   - Already booked: shows "Du er tilmeldt" with close button only
 *   - Success: brief confirmation, auto-close after 1.6s
 *
 * Supports inline: login, drop-in card/MobilePay payment, and guest booking
 * (flat flow — member covered or drop-in, plus paid guests via the same inline
 * session). Still handed off to the tenant booking page: participant names,
 * participant profiles (studios with participant_profiles_enabled), waivers,
 * first-time intake, membership purchase, category-based guests, and guests on
 * a klippekort-covered booking.
 */
export function BookingModal({ klass, initData, onClose, onBooked, guestsOnly }: Props) {
  const [user, setUser] = useState(authStore.getUser())
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<{ status: 'confirmed' | 'waitlist' } | null>(null)
  // Set when the API reports the class needs paying for and no membership covers
  // it — the widget can't take payment inline, so we redirect to the tenant site.
  const [needsPayment, setNeedsPayment] = useState(false)
  // OPG-0185: klippekort/abonnementer der kan dække holdet, og det kort kunden vil købe nu.
  const [coveringPlans, setCoveringPlans] = useState<CoveringPlan[]>([])
  const [purchasePlan, setPurchasePlan] = useState<CoveringPlan | null>(null)
  // Serveren svarede PAYMENT_CONSENT_REQUIRED: bookingen ender i en betaling, så ordre-
  // oversigten, fluebenet og den betalingspligtige knap vises før næste forsøg (jura-hs p44).
  const [needsPaymentConsent, setNeedsPaymentConsent] = useState(false)
  const [legal, setLegal] = useState<CheckoutLegalState>(initialCheckoutLegalState)
  // Sat når holdet kræver et aktivt medlemskab som kundens konto ikke har (eller
  // dækningen rammer ikke lige dette hold). Medlemskabskøb kan ikke gennemføres
  // inline, så vi skifter til et handoff til den native flade — præcis som
  // needsPayment. Et DÆKKET medlem lander aldrig her: serveren booker det inline,
  // så dette er grunden til at vi prøver bookingen i stedet for at redirecte blindt.
  const [needsMembership, setNeedsMembership] = useState(false)
  // Sat når holdet kræver deltagernavne — widgeten indsamler dem ikke, så vi
  // sender kunden til det native booking-flow (samme redirect som betaling).
  const [needsParticipantNames, setNeedsParticipantNames] = useState(false)
  // Sat når serveren svarer PARTICIPANT_PROFILE_REQUIRED (ældre init uden flaget).
  // Studier med deltagerprofiler booker hver plads til en profil, og profilvalget med
  // profilens abonnement, moms og alderstjek findes kun på den native flade.
  const [needsParticipantProfileFromServer, setNeedsParticipantProfileFromServer] = useState(false)
  // Sat når holdet kræver en accepteret ansvarsfraskrivelse. Widgeten kan
  // ikke registrere et juridisk samtykke (den har hverken teksten eller den
  // session accept-ruten kræver), så kunden sendes til den native flade.
  const [needsWaiver, setNeedsWaiver] = useState(false)
  // Sat første gang et hold stiller intake-spørgsmål. Har kunden svaret på
  // samme skabelon før, bærer serveren svarene med over, og vi lander aldrig
  // her — derfor rammer den kun første booking på den slags hold.
  const [needsIntake, setNeedsIntake] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  // Rabatkode: indtastes her og sendes med bookingen. Den valideres KUN
  // server-side (delt regelmotor med den native flade) — widgeten viser aldrig
  // selv en pris den har regnet ud, så en manipuleret klient kan ikke snyde.
  const [discountOpen, setDiscountOpen] = useState(false)
  const [discountCode, setDiscountCode] = useState('')
  // Gæste-gren (embed guest checkout): folder en lille form ud under login, så en
  // besøgende kan købe en drop-in uden konto når tenanten har slået
  // guestCheckoutEnabled til. Ved success får vi WIDGET-TOKENS (ikke en session)
  // tilbage, de lægges i authStore, og subscriptionen nedenfor flipper grenen.
  const [guestOpen, setGuestOpen] = useState(false)
  const [guestFirstName, setGuestFirstName] = useState('')
  const [guestLastName, setGuestLastName] = useState('')
  const [guestEmail, setGuestEmail] = useState('')
  const [guestPhone, setGuestPhone] = useState('')
  const [guestConsent, setGuestConsent] = useState(false)
  const [guestSubmitting, setGuestSubmitting] = useState(false)
  const [guestError, setGuestError] = useState<string | null>(null)
  // Inline gæste-stepper (fladt flow): hvor mange gæster medlemmet tager med.
  // Gæster betales ALTID med penge (holdets drop-in-pris pr. gæst) og dækkes aldrig
  // af abonnementet; derfor går en booking MED gæster altid gennem betal-først, selv
  // for et dækket medlem (serveren svarer requiresPayment → redirect til checkout).
  const [guestCount, setGuestCount] = useState(0)
  // Gæste-stepperen er et SEKUNDÆRT tilvalg: "book dig selv" er den primære handling,
  // så stepperen er foldet bag en "Tilføj gæster"-toggle (som rabatkode-toggle). Først
  // når kunden selv åbner den (eller der allerede er gæster) vises +/- felterne. Det
  // holder self-booking primær for de ~28 tenants der har gæster slået til, mens gæster
  // stadig er ét klik væk (vigtigt for fx Halgodt).
  const [guestStepperOpen, setGuestStepperOpen] = useState(false)
  // Sat når serveren ikke kan tage gæsterne inline (kategori-gæster, eller et
  // klippekort-dækket medlem) → handoff til den native flade.
  const [needsGuestHandoff, setNeedsGuestHandoff] = useState(false)
  // Sat når holdet er fuldt og kundens klippekort dækker det. Widgeten trækker ikke
  // klip på ventelisten, så kunden skriver sig på den på den native flade, hvor
  // klippet reserveres (ellers bad oprykningsmailen om drop-in-prisen kontant).
  const [needsWaitlistHandoff, setNeedsWaitlistHandoff] = useState(false)
  // Kundens valg af betalingsvej, når studiet har både MobilePay og kort.
  // MobilePay er forvalgt (samme standard som serverens 'auto').
  const [payMethod, setPayMethod] = useState<InlinePaymentMethod>('mobilepay')

  const s = t(initData.locale)
  const dateLocale = intlLocale(initData.locale)
  const tz = studietsTidszone(initData.timezone)

  useEffect(() => authStore.subscribe(setUser), [])
  // Et gæste-køb lever kun mens vinduet er åbent: lukkes det (afbrudt køb, en
  // overlevering til den native flade, eller efter bekræftelsen), rives gæste-
  // sessionen ned. Ved betalings-redirect forsvinder den med siden. Et rigtigt
  // medlems login røres ikke (endGuestSession gør intet uden en gæste-session).
  useEffect(() => () => authStore.endGuestSession(), [])

  // Drop-in-prisen serveren faktisk trækker den indloggede kunde, pr. person og pr.
  // gæst (fx uden moms for en under-30). Indtil den er hentet — og for en kunde der
  // ikke er logget ind — vises holdets listepris. Før viste vinduet altid listeprisen,
  // så en under-30 så "125 kr" og blev trukket 100 kr.
  const [dropInCheckoutPrice, setDropInCheckoutPrice] = useState<number | null>(null)
  // Gæsternes pris kan afvige fra kundens egen, når holdtypen har pris efter alder (OPG-0104).
  const [guestCheckoutPrice, setGuestCheckoutPrice] = useState<number | null>(null)
  // Hvad dækker kundens egen plads (abonnement eller et klip), med samme valg som bookingen.
  // Tog 21-testen 25-10-2026: vinduet viste drop-in-pris og betalingsvalg, mens bookingen trak
  // et klip. undefined = ikke hentet (ikke logget ind, eller opslaget fejlede): som før.
  const [coverage, setCoverage] = useState<WidgetCoverage | undefined>(undefined)
  const [coverageLookupPending, setCoverageLookupPending] = useState(false)
  // «Brug klippekort» (A4): kunden kan vælge drop-in i stedet for et klip på et drop-in-hold uden medlemskrav.
  const [brugKlip, setBrugKlip] = useState(true)
  const listDropInPrice = klass.dropInPriceDkk ?? 0
  const coverageRelevant = listDropInPrice > 0 || !!klass.membershipRequired
  useEffect(() => {
    if (!user || !coverageRelevant) {
      setDropInCheckoutPrice(null)
      setGuestCheckoutPrice(null)
      setCoverage(undefined)
      setCoverageLookupPending(false)
      return
    }
    let aktiv = true
    setCoverageLookupPending(true)
    apiClient
      .get<{ price: number; guestPrice?: number; coverage?: WidgetCoverage }>(`/api/widget/classes/price?classId=${encodeURIComponent(klass.id)}`, true)
      .then((result) => {
        if (!aktiv) return
        setCoverageLookupPending(false)
        if (typeof result.data?.price === 'number') setDropInCheckoutPrice(result.data.price)
        if (typeof result.data?.guestPrice === 'number') setGuestCheckoutPrice(result.data.guestPrice)
        if (result.data && 'coverage' in result.data) setCoverage(result.data.coverage ?? null)
      })
      .catch(() => { if (aktiv) setCoverageLookupPending(false) })
    return () => { aktiv = false }
  }, [user, klass.id, coverageRelevant])
  const dropInPrice = dropInCheckoutPrice ?? listDropInPrice

  // Har den indloggede kunde overhovedet et aktivt medlemskab? Kun på et almindeligt
  // members-only-hold (dækningen afgør adgangen). Tog 11, F33: vinduet sagde «Din plads er
  // dækket af dit medlemskab» + «Bekræft booking» til en kunde uden medlemskab, og først klikket
  // gav 403. Uden medlemskab går vi nu direkte til medlemskabs-handoffet; noten vises kun, når
  // kunden har et. Fejler opslaget, er status ukendt, og vi prøver bookingen som før.
  // Tog 13, M1: det samme gælder et members-only drop-in-hold («kun for medlemmer» +
  // «omfattes ikke af medlemskab»): kunden betaler drop-in, men SKAL have et aktivt
  // medlemskab (fælles regel membersOnlyAccess, som serveren og den native flade). Før
  // blev opslaget kun lavet for dæknings-hold, så en kunde uden medlemskab fik «Bekræft
  // og betal», og først klikket gav 403 MEMBERSHIP_REQUIRED. Mens opslaget kører, er
  // knappen deaktiveret (som på den native flade).
  const membersOnlyClass = membersOnlyAccess(klass, false) !== 'open'
  const [hasActiveMembership, setHasActiveMembership] = useState<boolean | null>(null)
  const [membershipLookupPending, setMembershipLookupPending] = useState(false)
  useEffect(() => {
    if (!user || !membersOnlyClass) {
      setHasActiveMembership(null)
      setMembershipLookupPending(false)
      return
    }
    let aktiv = true
    setMembershipLookupPending(true)
    apiClient
      .get<{ hasMembership: boolean }>('/api/widget/membership', true)
      .then((result) => {
        if (!aktiv) return
        setMembershipLookupPending(false)
        if (typeof result.data?.hasMembership !== 'boolean') return
        setHasActiveMembership(result.data.hasMembership)
        if (!result.data.hasMembership) setNeedsMembership(true)
      })
      .catch(() => { if (aktiv) setMembershipLookupPending(false) })
    return () => { aktiv = false }
  }, [user, membersOnlyClass])

  // Auto-close after success
  useEffect(() => {
    if (!success) return
    const timer = setTimeout(onClose, 1800)
    return () => clearTimeout(timer)
  }, [success, onClose])

  const isFull = klass.maxParticipants != null
    && klass.maxParticipants > 0
    && (klass.currentParticipants ?? 0) >= klass.maxParticipants
  const goesToWaitlist = isFull && klass.waitlistEnabled
  const isBlocked = isFull && !klass.waitlistEnabled
  // Deltagerprofiler på studiet: overlever til den native flade med det samme, før login
  // og gæste-køb. Den native side har login, gæste-køb og profilvalget. Et fuldt hold uden
  // venteliste viser stadig «fuldt» (der er intet at booke dér heller).
  const needsParticipantProfile = needsParticipantProfileFromServer
    || (!!initData.features?.participantProfilesEnabled && !isBlocked)

  // Gæste-køb kun på en RIGTIG drop-in: tenanten tillader det, holdet kræver ikke
  // medlemskab, det koster noget (betalingen er verifikationen af en rigtig
  // person) og det er ikke fuldt (et fuldt hold → venteliste, som kræver konto).
  // Serverens gate er den eneste håndhævelse der tæller; dette er kun UI.
  // OPG-0097: et udtrykkeligt «Gratis hold» kan også bookes som gæst, når studiet har slået det
  // til (core/guest-eligibility spejler serverens regel).
  const guestEligible = guestCheckoutAllowed(klass, initData.features) && !isFull

  // Inline gæste-stepper: holdet tillader gæster, der er en maks > 0, holdet er ikke
  // fuldt, og tenanten bruger IKKE gæste-kategorier (stepperen er kun til det flade
  // flow — kategorier falder tilbage til handoffet). Gælder BÅDE medlemskabs-hold (hvor
  // medlemmet er dækket og kun betaler for gæsten) og drop-in-hold. Kun UI; serveren
  // håndhæver reglerne. Vises først når brugeren er logget ind (final confirm-gren).
  const maxGuests = klass.maxGuests ?? 0
  const guestsInlineEligible = !!klass.guestsAllowed
    && maxGuests > 0
    && !isFull
    && !initData.features?.guestCategoriesEnabled
  // Gæste-enhedspris = holdets drop-in-pris (fladt flow), som serveren regner den for
  // kontohaveren. Vises i stepperen.
  const guestUnitPrice = guestCheckoutPrice ?? dropInPrice
  // Kan bookingen ende i en betaling? Betalte gæster, eller en drop-in-pris
  // medlemmet faktisk betaler (samme betingelse som pris-rækken nedenfor). Et
  // dækket medlem betaler alligevel intet — serveren ignorerer så metoden.
  // Venteliste tager ingen betaling.
  // Kundens egen plads betaler kun drop-in, når hverken abonnement eller klip dækker den.
  const klipKanFravaelges = !goesToWaitlist && kanFravaelgeKlip(klass, coverage)
  const valgtDaekning = daekningEfterValg(klass, coverage, brugKlip)
  const ownSpotPays = egenPladsBetalerDropIn(klass, valgtDaekning)
  // Mens dækningen hentes, venter knappen (kunden skal se, om et klip bruges, før hun bekræfter).
  // En gæste-session har ingen medlemskaber, så den venter ikke.
  const coverageBlocksConfirm = coverageLookupPending && !authStore.isGuestSession()
  const coveredClips = goesToWaitlist ? null : klipPaaHoldet(valgtDaekning)
  // Ordreoversigten før betaling (Tog 21-fund): holdets pris, gæsterne og «I alt inkl. moms»,
  // som på /pricing og booking-vinduet. Med en rabatkode regner serveren først beløbet ved
  // betalingen; totalen vises så som prisen FØR rabatkoden (et loft), ikke udeladt (FAL § 8).
  const egenPris = ownSpotPays ? dropInPrice : 0
  const gaestePris = guestCount > 0 && guestUnitPrice > 0 ? guestUnitPrice * guestCount : 0
  const orderLines: Array<{ label: string; amount?: string }> = [
    { label: klass.name, ...(egenPris > 0 ? { amount: formatMoney(egenPris, initData.locale, initData.currency) } : {}) },
    ...(gaestePris > 0 ? [{ label: `${s.guests} (${guestCount})`, amount: formatMoney(gaestePris, initData.locale, initData.currency) }] : []),
  ]
  const orderTotal = egenPris + gaestePris > 0
    ? formatMoney(egenPris + gaestePris, initData.locale, initData.currency)
    : undefined
  const orderTotalLabel = discountCode.trim() ? s.checkoutLegal.totalBeforeDiscount : undefined
  const mayPay = !goesToWaitlist && (
    (guestCount > 0 && guestUnitPrice > 0)
    || ownSpotPays
  )
  // Metode-vælgeren vises kun når der er noget at vælge imellem.
  const showPayPicker = mayPay && hasPaymentChoice(initData)
  // "Kun gæster"-tilstand: gæste-stepperen vises ÅBEN (gæste-først) i stedet for foldet
  // bag "+ Tilføj gæster". Aktiveres via widget-attributten guests-only="true".
  // Det er KUN framingen der ændres — medlemmet kan stadig booke sig selv uden gæster
  // (antal starter på 0), så ingen tvinges til at betale for en ikke-eksisterende gæst.
  const guestsOnlyMode = !!guestsOnly

  const tenantBaseUrl = tenantSiteUrl(initData)
  // Sømløs handoff til den native booking-flow: `?book=<classId>` er appens
  // kanoniske deep-link, som auto-åbner booking-modalen for netop dette hold på
  // tenant-siden (ClassesPageContent). Betaling/medlemskøb gennemføres dér, hvor
  // alle same-origin-værn (idempotens, kapacitet, moms, rate-limit) kører — i
  // stedet for at kopiere billing-kernen ind i bundlen. (`?classId=` ramte kun
  // siden uden at åbne modalen.)
  // Handoff med vejen hjem indbygget — se handoffUrl(). Dækker ALLE fire
  // overleveringer i modalen: medlemskab, deltagernavne, betaling og waiver.
  const tenantBookingUrl = handoffUrl(tenantBaseUrl, `/booking?book=${klass.id}`)

  // «Dette hold skal betales» eller «kræver medlemskab»: hent de klippekort/abonnementer, der kan
  // dække holdet, så kunden kan købe et kort (OPG-0185). Et hold der KUN kan bookes med klippekort
  // (medlemskab påkrævet) viser kun klippekort (Thomas 03-11). Read-only og uden login; fejl/tomt svar = som før.
  useEffect(() => {
    if (!needsPayment && !needsMembership) return
    let aktiv = true
    const art = !needsPayment && needsMembership ? '&kind=klippekort' : ''
    apiClient.get<unknown>(`/api/widget/class-plans?classId=${encodeURIComponent(klass.id)}${art}`)
      .then(res => { if (aktiv && !res.error) setCoveringPlans(parseCoveringPlans(res.data)) })
      .catch(() => undefined)
    return () => { aktiv = false }
  }, [needsPayment, needsMembership, klass.id])

  async function handleConfirm() {
    if (submitting) return
    setSubmitting(true)
    setError(null)

    const result = await apiClient.post<BookingResponse>('/api/widget/bookings', {
      classIds: [klass.id],
      // Tom streng udelades, så en uåbnet rabat-sektion aldrig sender et felt.
      // Rabatkode + gæster kombineres IKKE inline (gæste-sessionen prissætter ikke
      // rabat); rabat-sektionen er allerede skjult når guestCount > 0, og vi dropper
      // feltet her som ekstra værn mod en tabt rabat.
      ...(guestCount === 0 && discountCode.trim() ? { discountCode: discountCode.trim() } : {}),
      // Bed om inline betaling hvis holdet er priced+udækket. Har studiet både
      // MobilePay og kort, sender vi kundens valg fra metode-vælgeren (serveren
      // respekterer et eksplicit valg). Ellers lader 'auto' SERVEREN vælge på
      // tenantens egne nøgler (MobilePay når studiet har begge, ellers Stripe).
      // Dækkede/gratis hold ignorerer feltet og bookes som før; kan serveren ikke
      // tage betaling, svarer den PAYMENT_REQUIRED → redirect-handoff nedenfor.
      // (Tidligere hardkodet 'stripe' → MobilePay-only-studier kunne aldrig betale
      // drop-in inline.)
      paymentMethod: showPayPicker ? payMethod : 'auto',
      // Gæster (fladt flow): medlem + betalte gæster. Udelades når 0, så en almindelig
      // booking er uændret. Serveren validerer antal mod skabelonens maks og tager
      // gæste-beløbet via samme inline-session → requiresPayment-redirect nedenfor.
      ...(guestCount > 0 ? { guestCount } : {}),
      // Fravalgt klippekort: pladsen betales som drop-in (serveren bruger så intet klip).
      ...(klipKanFravaelges && !brugKlip ? { useKlippekort: false } : {}),
      // Vejen hjem. Den inline betaling springer /widget-retur over (vi går
      // direkte til Stripe), så uden dette felt kunne returcookien ikke sættes
      // og kunden landede på VORES forside efter en afbrudt betaling.
      // Serveren validerer adressen mod studiets godkendte widget-oprindelser.
      ...(returMaal() ? { returnUrl: returMaal() } : {}),
      legalConsent: checkoutConsent(legal, initData.locale),
    }, true)

    setSubmitting(false)

    if (result.error) {
      // Bookingen skal betales: vis oversigt + flueben, og lad kunden bekræfte igen.
      if (result.code === 'PAYMENT_CONSENT_REQUIRED') {
        setNeedsPaymentConsent(true)
        return
      }
      // AUTH_REQUIRED is rare (button hidden when logged out) but handle it.
      if (result.code === 'AUTH_REQUIRED') {
        authStore.clearTokens()
        return
      }
      // Priced drop-in with no covering membership: the widget can't take card
      // payment inline, so swap the modal to a "continue to checkout" redirect
      // (same pattern as membershipRequired). The tenant booking page runs the
      // full MobilePay/Stripe/Flatpay flow.
      if (result.code === 'PAYMENT_REQUIRED') {
        setNeedsPayment(true)
        return
      }
      // Holdet kræver et aktivt medlemskab kontoen ikke har (eller dækningen rammer
      // ikke dette hold). Medlemskabskøb kan ikke køre inline, så vi overleverer til
      // den native flade. En dækket kunde når aldrig hertil (serveren booker inline).
      if (result.code === 'MEMBERSHIP_REQUIRED') {
        setNeedsMembership(true)
        return
      }
      // Gæsterne kan ikke tages inline (kategori-gæster eller klippekort-dækket medlem)
      // → handoff til den native flade, hvor gæste-vælgeren og klip-fradraget bor.
      if (result.code === 'GUESTS_NEED_NATIVE') {
        setNeedsGuestHandoff(true)
        return
      }
      // Fuldt hold og et klippekort der dækker det → ventelisten på den native flade.
      if (result.code === 'WAITLIST_NEEDS_NATIVE') {
        setNeedsWaitlistHandoff(true)
        return
      }
      // Ansvarsfraskrivelsen mangler — kun den native flade kan tage imod accepten.
      if (result.code === 'WAIVER_REQUIRED') {
        setNeedsWaiver(true)
        return
      }
      // Intake-spørgsmål der ikke kan besvares her — kun den native flade har felterne.
      if (result.code === 'INTAKE_REQUIRED') {
        setNeedsIntake(true)
        return
      }
      // Studiet booker hver plads til en deltagerprofil — kun det native flow har profilvalget.
      if (result.code === 'PARTICIPANT_PROFILE_REQUIRED') {
        setNeedsParticipantProfileFromServer(true)
        return
      }
      // Holdet kræver deltagernavne — kun det native flow indsamler dem.
      if (result.code === 'PARTICIPANT_NAMES_REQUIRED') {
        setNeedsParticipantNames(true)
        return
      }
      // Rabat-fejl er stabile koder (ALDRIG tekst-match) — de fire hyppigste
      // får rigtig oversættelse, resten falder tilbage på den generiske.
      if (result.code && result.code.startsWith('DISCOUNT_')) {
        const byCode: Record<string, string> = {
          DISCOUNT_EXPIRED: s.discountExpired,
          DISCOUNT_USED_UP: s.discountUsedUp,
          DISCOUNT_ALREADY_USED: s.discountAlreadyUsed,
          // Koden findes og er gyldig — den gælder bare ikke hold. Den generiske
          // «ikke gyldig» var direkte misvisende her.
          DISCOUNT_NOT_FOR_CLASSES: s.discountNotForClasses,
        }
        setError(byCode[result.code] || s.discountInvalid)
        setDiscountOpen(true)
        return
      }
      // Throttling af kodeforsøg. Uden denne gren faldt beskeden igennem til
      // serverens ENGELSKE tekst midt i et ellers oversat flow.
      if (result.code === 'RATE_LIMITED') {
        setError(s.discountTooManyAttempts)
        setDiscountOpen(true)
        return
      }
      setError(result.error)
      return
    }

    // Inline drop-in: serveren har oprettet en pending-booking + Stripe-session.
    // Send kunden til Stripes hosted checkout (bekræftes af webhooken efter betaling).
    if (result.data?.requiresPayment && result.data.redirectUrl) {
      navigerUd(result.data.redirectUrl)
      return
    }

    if (result.data?.success) {
      const status: 'confirmed' | 'waitlist' = (result.data.waitlistCount ?? 0) > 0
        ? 'waitlist' : 'confirmed'
      setSuccess({ status })
      onBooked?.({
        confirmedCount: result.data.confirmedCount ?? 0,
        waitlistCount: result.data.waitlistCount ?? 0,
      })
    }
  }

  // Afmeld en eksisterende booking. Widgeten kender kun holdet, så vi slår
  // brugerens booking for netop dette hold op og kalder DELETE-endpointet, der
  // server-side håndterer afmeldingsfrist, klip-refusion, venteliste-promotion
  // og sen-afmeldings-gebyr (samme regler som medlemsområdet).
  async function handleCancel() {
    if (cancelling) return
    if (!window.confirm(s.confirmUnsubscribe.replace('{name}', klass.name))) return
    setCancelling(true)
    setError(null)

    const list = await apiClient.get<Array<{ id: string; class: { id: string } | null }>>(
      '/api/widget/bookings', true,
    )
    if (list.error || !list.data) {
      setCancelling(false)
      setError(list.error || s.loadError)
      return
    }
    const mine = list.data.find(b => b.class?.id === klass.id)
    if (!mine) {
      setCancelling(false)
      setError(s.loadError)
      return
    }

    const res = await apiClient.delete(`/api/widget/bookings/${mine.id}`, true)
    setCancelling(false)
    if (res.error) {
      // SELF_SERVICE_LOCKED m.fl. kommer allerede lokaliseret fra serveren.
      setError(res.error)
      return
    }
    // Refresh the parent list (so the class drops its "Booket" state) and close.
    onBooked?.({ confirmedCount: 0, waitlistCount: 0 })
    onClose()
  }

  // Gæste-køb: opret/genbrug en kodeordsløs bruger server-side og få WIDGET-TOKENS
  // retur (ikke en session). Anonymt kald — præcis som LoginWidget's register.
  async function handleGuestSubmit(e: Event) {
    e.preventDefault()
    if (guestSubmitting) return
    // Samtykket er obligatorisk og tjekkes klient-side FØR kaldet, så vi ikke
    // sender et kald der alligevel afvises.
    if (!guestConsent) {
      setGuestError(s.guestConsentRequired)
      return
    }
    setGuestSubmitting(true)
    setGuestError(null)

    const result = await apiClient.post<GuestSessionResponse>('/api/widget/guest-session', {
      classIds: [klass.id],
      firstName: guestFirstName,
      lastName: guestLastName,
      email: guestEmail,
      // Tom streng udelades (feltet er valgfrit).
      ...(guestPhone.trim() ? { phone: guestPhone.trim() } : {}),
      acceptedTerms: true,
    })

    setGuestSubmitting(false)

    if (result.error) {
      // Match ALDRIG på serverens tekst — kun på den stabile kode.
      setGuestError(
        result.code === 'ACCOUNT_EXISTS' ? s.guestAccountExists
          : result.code === 'GUEST_LINK_SENT' ? s.guestLinkSent
          : result.code && GUEST_SERVER_TEXT_CODES.includes(result.code) && result.error ? result.error
          : s.guestError,
      )
      return
    }

    if (result.data) {
      // authStore.subscribe ovenfor sætter `user`, og grenen skifter selv til
      // booking-trinet — den besøgende forlader aldrig siden. Gæste-tokens holdes
      // kun i hukommelsen (aldrig localStorage), se authStore.setGuestSession.
      authStore.setGuestSession(result.data.accessToken, result.data.refreshToken, result.data.user)
    }
  }

  // Stop propagation so clicks inside the dialog don't bubble to backdrop.
  const stop = (e: Event) => e.stopPropagation()

  // OPG-0185: kunden har valgt et klippekort. Det eksisterende købsvindue (login, rabatkode,
  // ordreoversigt, betaling, vejen hjem) overtager; lukkes det, er kunden tilbage her. Der
  // bookes ikke automatisk efter købet. Beløbet regner serveren selv i checkout, aldrig denne pris.
  if (purchasePlan) {
    return (
      <PurchaseModal
        plan={{
          id: purchasePlan.id,
          name: purchasePlan.name,
          price: purchasePlan.price,
          membershipCategory: purchasePlan.membershipCategory,
          validityDays: purchasePlan.validityDays,
          activatesFrom: purchasePlan.activatesFrom,
        }}
        initData={initData}
        purchaseHref={handoffUrl(tenantBaseUrl, `/pricing?plan=${purchasePlan.id}&autoStart=1`)}
        onClose={() => setPurchasePlan(null)}
      />
    )
  }

  return (
    <div class="hsw-bm-backdrop" onClick={onClose}>
      <style>{modalStyles}{classPlanChoiceStyles}</style>
      <div class="hsw-bm-dialog" onClick={stop} role="dialog" aria-modal="true" aria-labelledby="hsw-bm-title">
        <button class="hsw-bm-close" onClick={onClose} aria-label={s.close}>×</button>

        <div class="hsw-bm-meta">
          {fmtDay(klass.startTime, dateLocale, tz)} · {fmtTime(klass.startTime, dateLocale, tz)}–{fmtTime(klass.endTime, dateLocale, tz)}
        </div>
        <h3 id="hsw-bm-title" class="hsw-bm-title">{klass.name}</h3>
        {klass.instructorName && <div class="hsw-bm-detail">{klass.instructorName}</div>}
        {klass.room && <div class="hsw-bm-detail">{klass.room}</div>}
        {klass.description && (
          <p class="hsw-bm-desc">{klass.description}</p>
        )}

        {/* Already booked — short-circuit */}
        {klass.isBooked ? (
          <div class="hsw-bm-state hsw-bm-state-booked">
            <span class="hsw-bm-check">✓</span>
            <strong>{s.alreadyBookedTitle}</strong>
            <p>{s.alreadyBookedBody}</p>
            {error && <div class="hsw-bm-error" role="alert">{error}</div>}
            <button class="hsw-bm-cancel" onClick={handleCancel} disabled={cancelling}>
              {cancelling ? '…' : s.unsubscribe}
            </button>
            <button class="hsw-bm-secondary" onClick={onClose} disabled={cancelling}>{s.close}</button>
          </div>
        ) : success ? (
          <div class="hsw-bm-state hsw-bm-state-success">
            <span class="hsw-bm-check">✓</span>
            <strong>{success.status === 'waitlist' ? s.waitlistTitle : s.confirmedTitle}</strong>
            <p>{success.status === 'waitlist' ? s.waitlistBody : s.confirmedBody}</p>
          </div>
        ) : needsParticipantProfile ? (
          // Studiet booker hver plads til en deltagerprofil (som den native flade kræver).
          // Widgeten har ingen profil-vælger, så kunden vælger deltager på ?book=<id>.
          <div class="hsw-bm-state" data-hsw-participant-profile-handoff>
            <p>{s.participantProfileBody}</p>
            <a class="hsw-bm-primary" href={tenantBookingUrl} target="_blank" rel="noopener">
              {s.continueToBooking}
            </a>
            <button class="hsw-bm-secondary" onClick={onClose}>{s.cancel}</button>
          </div>
        ) : !user ? (
          // Inline login: LoginWidget writes to authStore, the subscription above
          // picks it up, and this branch swaps itself for the booking step. The
          // visitor never leaves the site they are already on.
          <div class="hsw-bm-state hsw-bm-state-login">
            {guestEligible
              ? <p>{s.guestOrLoginPrompt}</p>
              : <p>{s.loginOrSignUpToBook} <strong>{klass.name}</strong>.</p>}
            <LoginWidget initData={initData} />
            {/* Gæste-køb: tilbydes UDOVER login, kun på drop-in-berettigede hold. */}
            {guestEligible && (
              <div class="hsw-bm-guest">
                {guestOpen ? (
                  <form class="hsw-bm-guest-form" onSubmit={handleGuestSubmit} noValidate>
                    <p class="hsw-bm-guest-intro">{s.guestIntro}</p>
                    <div class="hsw-bm-guest-row">
                      <div class="hsw-bm-guest-field">
                        <label class="hsw-bm-guest-label" for="hsw-bm-guest-firstname">{s.fieldFirstName}</label>
                        <input
                          id="hsw-bm-guest-firstname"
                          class="hsw-bm-guest-input"
                          type="text"
                          value={guestFirstName}
                          onInput={(e) => setGuestFirstName((e.target as HTMLInputElement).value)}
                          required
                          autocomplete="given-name"
                          disabled={guestSubmitting}
                        />
                      </div>
                      <div class="hsw-bm-guest-field">
                        <label class="hsw-bm-guest-label" for="hsw-bm-guest-lastname">{s.fieldLastName}</label>
                        <input
                          id="hsw-bm-guest-lastname"
                          class="hsw-bm-guest-input"
                          type="text"
                          value={guestLastName}
                          onInput={(e) => setGuestLastName((e.target as HTMLInputElement).value)}
                          required
                          autocomplete="family-name"
                          disabled={guestSubmitting}
                        />
                      </div>
                    </div>
                    <div class="hsw-bm-guest-field">
                      <label class="hsw-bm-guest-label" for="hsw-bm-guest-email">{s.fieldEmail}</label>
                      <input
                        id="hsw-bm-guest-email"
                        class="hsw-bm-guest-input"
                        type="email"
                        value={guestEmail}
                        onInput={(e) => setGuestEmail((e.target as HTMLInputElement).value)}
                        required
                        autocomplete="email"
                        placeholder={s.emailPlaceholder}
                        disabled={guestSubmitting}
                      />
                    </div>
                    <div class="hsw-bm-guest-field">
                      <label class="hsw-bm-guest-label" for="hsw-bm-guest-phone">
                        {s.fieldPhone} <span class="hsw-bm-guest-optional">{s.optionalSuffix}</span>
                      </label>
                      <input
                        id="hsw-bm-guest-phone"
                        class="hsw-bm-guest-input"
                        type="tel"
                        value={guestPhone}
                        onInput={(e) => setGuestPhone((e.target as HTMLInputElement).value)}
                        autocomplete="tel"
                        disabled={guestSubmitting}
                      />
                    </div>
                    <label class="hsw-bm-guest-consent">
                      <input
                        type="checkbox"
                        checked={guestConsent}
                        onChange={(e) => setGuestConsent((e.target as HTMLInputElement).checked)}
                        disabled={guestSubmitting}
                      />
                      <span>
                        {s.guestConsent}{' '}
                        <a class="hsw-bm-guest-legal" href={legalDocumentUrl(initData, 'terms')} target="_blank" rel="noopener noreferrer">{s.guestTermsLink}</a>
                        {' · '}
                        <a class="hsw-bm-guest-legal" href={legalDocumentUrl(initData, 'privacy')} target="_blank" rel="noopener noreferrer">{s.guestPrivacyLink}</a>
                      </span>
                    </label>
                    <p class="hsw-bm-guest-hint">{s.guestCoverageHint}</p>
                    {guestError && <div class="hsw-bm-error" role="alert">{guestError}</div>}
                    <button type="submit" class="hsw-bm-primary" disabled={guestSubmitting}>
                      {guestSubmitting ? s.guestSubmitting : s.guestSubmit}
                    </button>
                  </form>
                ) : (
                  <button
                    type="button"
                    class="hsw-bm-guest-toggle"
                    onClick={() => setGuestOpen(true)}
                  >
                    {s.guestOption}
                  </button>
                )}
              </div>
            )}
            <button class="hsw-bm-secondary" onClick={onClose}>{s.cancel}</button>
          </div>
        ) : isBlocked ? (
          <div class="hsw-bm-state hsw-bm-state-error">
            <strong>{s.fullTitle}</strong>
            <p>{s.fullBody}</p>
            <button class="hsw-bm-secondary" onClick={onClose}>{s.close}</button>
          </div>
        ) : needsMembership ? (
          // Serveren meldte at holdet kræver et aktivt medlemskab kontoen ikke har.
          // Medlemskabskøb kan ikke gennemføres inline, så vi overleverer til den
          // native booking-flade (samme redirect-mønster som needsPayment). Et
          // dækket medlem rammer ALDRIG denne gren — serveren booker det inline, så
          // vi viser "Bekræft booking" nedenfor og prøver bookingen frem for at
          // redirecte blindt på klass.membershipRequired (som før punkterede ethvert
          // medlems inline-booking).
          <div class="hsw-bm-state">
            <p>{s.membershipRequiredBody}</p>
            <a class="hsw-bm-primary" href={tenantBookingUrl} target="_blank" rel="noopener">
              {s.continueToBooking}
            </a>
            {/* Klippekort-only: kun de klippekort der dækker holdet (Thomas 03-11), aldrig abonnementer. */}
            <ClassPlanChoice plans={coveringPlans} initData={initData} tenantBaseUrl={tenantBaseUrl} onBuy={setPurchasePlan} kunKlippekort />
            <button class="hsw-bm-secondary" onClick={onClose}>{s.cancel}</button>
          </div>
        ) : needsIntake ? (
          // Første booking på et hold med intake-spørgsmål. Widgetens form har
          // ikke felterne, så hun svarer på den native flade — én gang.
          <div class="hsw-bm-state">
            <p>{s.intakeRequiredBody}</p>
            <a class="hsw-bm-primary" href={tenantBookingUrl} target="_blank" rel="noopener">
              {s.continueToBooking}
            </a>
            <button class="hsw-bm-secondary" onClick={onClose}>{s.cancel}</button>
          </div>
        ) : needsWaiver ? (
          // Holdet kræver en accepteret ansvarsfraskrivelse. Accepten skal
          // registreres med tekst-snapshot, IP og user-agent, så den sker på
          // den native flade (?book=<id>) — ikke her.
          <div class="hsw-bm-state">
            <p>{s.waiverRequiredBody}</p>
            <a class="hsw-bm-primary" href={tenantBookingUrl} target="_blank" rel="noopener">
              {s.continueToBooking}
            </a>
            <button class="hsw-bm-secondary" onClick={onClose}>{s.cancel}</button>
          </div>
        ) : needsParticipantNames ? (
          // Class collects attendee names — the widget's simple form doesn't, so
          // hand off to the native booking flow (?book=<id>) which does.
          <div class="hsw-bm-state">
            <p>{s.participantNamesBody}</p>
            <a class="hsw-bm-primary" href={tenantBookingUrl} target="_blank" rel="noopener">
              {s.continueToBooking}
            </a>
            <button class="hsw-bm-secondary" onClick={onClose}>{s.cancel}</button>
          </div>
        ) : needsPayment ? (
          // Priced drop-in with no covering membership — send the visitor to the
          // tenant's own checkout (card payment can't run inside the widget).
          <div class="hsw-bm-state">
            {klass.dropInPriceDkk != null && klass.dropInPriceDkk > 0 && (
              <div class="hsw-bm-price">
                <span class="hsw-bm-price-label">{s.dropInPrice}</span>
                <span class="hsw-bm-price-amount">{formatMoney(dropInPrice, initData.locale, initData.currency)}</span>
              </div>
            )}
            <p>{s.paymentRequiredBody}</p>
            <a class="hsw-bm-primary" href={tenantBookingUrl} target="_blank" rel="noopener">
              {coveringPlans.length > 0 ? s.classPlansPaySingle : s.continueToBooking}
            </a>
            <ClassPlanChoice plans={coveringPlans} initData={initData} tenantBaseUrl={tenantBaseUrl} onBuy={setPurchasePlan} />
            <button class="hsw-bm-secondary" onClick={onClose}>{s.cancel}</button>
          </div>
        ) : needsWaitlistHandoff ? (
          // Fuldt hold og kundens klippekort dækker det: ventelisten med reserveret
          // klip sker på den native flade (?book=<id>), ikke her.
          <div class="hsw-bm-state">
            <p>{s.waitlistClipCardBody}</p>
            <a class="hsw-bm-primary" href={tenantBookingUrl} target="_blank" rel="noopener">
              {s.continueToBooking}
            </a>
            <button class="hsw-bm-secondary" onClick={onClose}>{s.cancel}</button>
          </div>
        ) : needsGuestHandoff ? (
          // Gæsterne kan ikke tages inline (kategori-gæster eller klippekort-dækket
          // medlem) → handoff til den native flade, hvor gæste-vælgeren/klip-fradraget bor.
          <div class="hsw-bm-state">
            <a class="hsw-bm-primary" href={tenantBookingUrl} target="_blank" rel="noopener">
              {s.bookWithGuests}
            </a>
            <button class="hsw-bm-secondary" onClick={onClose}>{s.cancel}</button>
          </div>
        ) : (
          <div class="hsw-bm-state">
            {/* Særlig afmeldingsfrist: kun når holdets frist afviger fra den generelle. */}
            {klass.ownCancellationHours != null && (
              <div
                class="hsw-bm-own-cancellation"
                style="background:#fffbeb;border:1px solid #fcd34d;border-radius:6px;padding:8px 12px;margin-bottom:12px;font-size:13px;line-height:1.5;color:#92400e"
              >
                <strong>{s.ownCancellationHeading}</strong>{' '}
                {klass.ownCancellationHours === 0
                  ? s.ownCancellationNoRefund
                  : (klass.ownCancellationHours === 1 ? s.ownCancellationDeadlineOne : s.ownCancellationDeadline)
                      .replace('{count}', String(klass.ownCancellationHours))}
              </div>
            )}
            {/* Klip-pris: kun når holdet koster mere end standard-1-klip og er
                klip-berettiget — så et 2-klip-hold ikke ser ud som 1 klip. */}
            {coveredClips == null && klass.clipCost != null && klass.clipCost > 1 && !klass.excludeFromMembership && !klass.isFree && (
              <div class="hsw-bm-price">
                <span class="hsw-bm-price-label">{s.price}</span>
                <span class="hsw-bm-price-amount">{plural(s.clipCostShort, klass.clipCost, initData.locale)}</span>
              </div>
            )}
            {/* Drop-in-pris vises når medlemmet FAKTISK betaler den: på et almindeligt
                drop-in-hold (!membershipRequired) ELLER på et medlemskabs-krævende hold
                der er excludeFromMembership (betales også af medlemmer, fx Saunagus 150 kr).
                Et medlemskabs-DÆKKET hold (membershipRequired uden exclude) er gratis og
                skjuler prisen. */}
            {/* Et klip dækker kundens plads: som booking-vinduet på hjemmesiden, i stedet for
                drop-in-prisen, rabatkoden og betalingsvalget. */}
            {coveredClips != null && (
              <div class="hsw-bm-price hsw-bm-covered-clip">
                <span class="hsw-bm-price-label">{s.coveredByClipCard}</span>
                <span class="hsw-bm-price-amount">{plural(s.clipCostShort, coveredClips, initData.locale)}</span>
              </div>
            )}
            {klipKanFravaelges && (
              <label class="hsw-bm-use-clip" style="display:flex;align-items:center;gap:8px;margin:6px 0;cursor:pointer">
                <input
                  type="checkbox"
                  checked={brugKlip}
                  onChange={(e) => setBrugKlip((e.target as HTMLInputElement).checked)}
                  style="width:18px;height:18px"
                />
                <span>{s.useClipCard}</span>
              </label>
            )}
            {/* Et abonnement dækker et drop-in-hold: sig det i stedet for at vise prisen. */}
            {!goesToWaitlist && coverage?.kind === 'subscription' && paysDropIn(klass) && !klass.excludeFromMembership && (
              <div class="hsw-bm-member-covered">{s.coveredBySubscription}</div>
            )}
            {ownSpotPays && (
              <div class="hsw-bm-price">
                <span class="hsw-bm-price-label">{s.dropInPrice}</span>
                <span class="hsw-bm-price-amount">{formatMoney(dropInPrice, initData.locale, initData.currency)}</span>
              </div>
            )}
            {/* Kun på betalte drop-in-hold: en venteliste-plads eller et
                medlemskabsdækket hold har ingen pris at give rabat på. Skjules når
                der er tilføjet gæster — rabat + gæster kombineres ikke inline. */}
            {!goesToWaitlist && !klass.membershipRequired && guestCount === 0 && ownSpotPays && (
              <div class="hsw-bm-discount">
                {discountOpen ? (
                  <input
                    class="hsw-bm-discount-input"
                    type="text"
                    value={discountCode}
                    placeholder={s.discountCodePlaceholder}
                    aria-label={s.discountCodePlaceholder}
                    autocomplete="off"
                    autocapitalize="characters"
                    onInput={(e) => setDiscountCode((e.target as HTMLInputElement).value)}
                  />
                ) : (
                  <button
                    type="button"
                    class="hsw-bm-discount-toggle"
                    onClick={() => setDiscountOpen(true)}
                  >
                    {s.discountCodeToggle}
                  </button>
                )}
              </div>
            )}
            {/* Medlemskabs-hold: gør eksplicit at medlemmets EGEN plads er gratis, så en
                gæste-betaling ikke ligner en opkrævning for medlemmet selv. Vises kun når
                gæster er mulige (det er dér forvirringen om "hvem betaler" opstår). På et
                drop-in-hold vises medlemmets egen drop-in-pris i stedet (ovenfor). */}
            {klass.membershipRequired && !klass.excludeFromMembership && guestsInlineEligible && hasActiveMembership === true && coveredClips == null && (
              <div class="hsw-bm-member-covered">{s.memberSpotCovered}</div>
            )}
            {/* Inline gæste-stepper (fladt flow): medlem + betalte gæster. Vises på
                BÅDE medlemskabs-hold (medlem dækket, betaler kun gæsten) og drop-in.
                Gæste-prisen = holdets drop-in-pris pr. gæst. Ved bekræftelse sender
                serveren gæste-beløbet via inline-session → redirect til betaling. */}
            {guestsInlineEligible && (
              <div class="hsw-bm-guests">
                {!guestsOnlyMode && !guestStepperOpen && guestCount === 0 ? (
                  // Sekundært opt-in: self-booking forbliver primær; gæster er ét klik væk.
                  // I "kun gæster"-tilstand (guestsOnlyMode) springes toggle'en over, så
                  // stepperen altid vises åben (gæste-først framing).
                  <button
                    type="button"
                    class="hsw-bm-guests-add"
                    onClick={() => setGuestStepperOpen(true)}
                  >
                    + {s.addGuests}
                  </button>
                ) : (
                  <>
                <div class="hsw-bm-guests-row">
                  <div class="hsw-bm-guests-label">
                    <span>{s.guests}</span>
                    {guestUnitPrice > 0 && (
                      <span class="hsw-bm-guests-unit">
                        {formatMoney(guestUnitPrice, initData.locale, initData.currency)} {s.perGuest}
                      </span>
                    )}
                  </div>
                  <div class="hsw-bm-guests-stepper">
                    <button
                      type="button"
                      class="hsw-bm-guests-btn"
                      onClick={() => setGuestCount(Math.max(0, guestCount - 1))}
                      disabled={submitting || guestCount <= 0}
                      aria-label={s.guests}
                    >−</button>
                    <span class="hsw-bm-guests-count" aria-live="polite">{guestCount}</span>
                    <button
                      type="button"
                      class="hsw-bm-guests-btn"
                      onClick={() => setGuestCount(Math.min(maxGuests, guestCount + 1))}
                      disabled={submitting || guestCount >= maxGuests}
                      aria-label={s.guests}
                    >+</button>
                  </div>
                </div>
                {guestCount > 0 && guestUnitPrice > 0 && (
                  <div class="hsw-bm-price">
                    <span class="hsw-bm-price-label">{s.guests} ({guestCount})</span>
                    <span class="hsw-bm-price-amount">
                      {formatMoney(guestUnitPrice * guestCount, initData.locale, initData.currency)}
                    </span>
                  </div>
                )}
                  </>
                )}
              </div>
            )}
            {/* MobilePay eller kort — kun når studiet har begge og der kan blive
                noget at betale. */}
            {showPayPicker && (
              <PaymentMethodPicker
                initData={initData}
                value={payMethod}
                onChange={setPayMethod}
                disabled={submitting}
              />
            )}
            {needsPaymentConsent && (
              <CheckoutLegal
                s={s.checkoutLegal}
                initData={initData}
                lines={orderLines}
                total={orderTotal}
                totalLabel={orderTotalLabel}
                noWithdrawalRight
                state={legal}
                onChange={setLegal}
                disabled={submitting}
              />
            )}
            {error && <div class="hsw-bm-error" role="alert">{error}</div>}
            <button
              class="hsw-bm-primary"
              onClick={handleConfirm}
              disabled={submitting || membershipLookupPending || coverageBlocksConfirm || (needsPaymentConsent && !legal.termsAccepted)}
            >
              {submitting ? s.booking : needsPaymentConsent ? s.checkoutLegal.payGeneric : (goesToWaitlist ? s.joinWaitlist : (
                (guestCount > 0 && guestUnitPrice > 0) || (klass.excludeFromMembership && (klass.dropInPriceDkk ?? 0) > 0)
                  ? s.confirmAndPay : s.confirmBooking))}
            </button>
            <button class="hsw-bm-secondary" onClick={onClose} disabled={submitting}>{s.cancel}</button>
            {/* Gæste-handoff: kun når den inline stepper IKKE er mulig (kategori-gæster).
                Det flade flow håndteres af stepperen ovenfor; så undgår vi to gæste-veje. */}
            {klass.guestsAllowed && !guestsInlineEligible && (
              <a class="hsw-bm-guest-link" href={tenantBookingUrl} target="_blank" rel="noopener">
                {s.bookWithGuests}
              </a>
            )}
            {/* Book hele holdet: betaling kan ikke køre inline i widgeten, så
                send kunden til det native flow (som priced drop-in / gæster). */}
            {klass.fullVenueAvailable && klass.fullVenuePriceDkk != null && klass.fullVenuePriceDkk > 0 && (
              <a class="hsw-bm-guest-link" href={tenantBookingUrl} target="_blank" rel="noopener">
                {s.fullVenueButton} · {formatMoney(klass.fullVenuePriceDkk, initData.locale, initData.currency)}
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

const modalStyles = `
  .hsw-bm-discount {
    margin: 0 0 0.75rem 0;
  }
  .hsw-bm-discount-toggle {
    font: inherit;
    font-size: 0.875rem;
    background: none;
    border: 0;
    padding: 0;
    color: inherit;
    opacity: 0.7;
    text-decoration: underline;
    cursor: pointer;
  }
  .hsw-bm-discount-toggle:hover {
    opacity: 1;
  }
  .hsw-bm-discount-input {
    font: inherit;
    width: 100%;
    padding: 0.6rem 0.75rem;
    border: 1px solid var(--yoga-border, #d4d4d4);
    border-radius: var(--yoga-radius, 0);
    background: transparent;
    color: inherit;
    text-transform: uppercase;
  }
  .hsw-bm-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 100000;
    padding: 1rem;
    animation: hsw-bm-fade .15s ease-out;
  }
  @keyframes hsw-bm-fade {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  .hsw-bm-dialog {
    position: relative;
    background: var(--yoga-card-bg, #ffffff);
    border-radius: var(--yoga-radius, 8px);
    padding: 2rem 1.75rem 1.75rem;
    width: 100%;
    max-width: 480px;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 20px 60px rgba(0,0,0,0.2);
    color: var(--yoga-text, #0a0a0a);
    box-sizing: border-box;
  }
  .hsw-bm-close {
    position: absolute;
    top: 0.5rem;
    right: 0.75rem;
    background: transparent;
    border: 0;
    font-size: 1.75rem;
    line-height: 1;
    color: var(--yoga-text-muted, #737373);
    cursor: pointer;
    padding: 0.25rem 0.6rem;
  }
  .hsw-bm-close:hover { color: var(--yoga-text, #0a0a0a); }
  .hsw-bm-meta {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--yoga-text-muted, #737373);
    font-weight: 600;
    margin-bottom: 0.4rem;
  }
  .hsw-bm-title {
    font-size: 1.4rem;
    font-weight: 700;
    margin: 0 0 0.6rem;
    letter-spacing: -0.01em;
    line-height: 1.25;
  }
  .hsw-bm-detail {
    font-size: 0.9rem;
    color: var(--yoga-text-muted, #525252);
    margin-bottom: 0.25rem;
  }
  .hsw-bm-desc {
    font-size: 0.9rem;
    color: var(--yoga-text-muted, #525252);
    line-height: 1.5;
    margin: 0.75rem 0 0;
    /* Holdets fulde beskrivelse med linjeskift (skema-widgettens kort viser kun 2 linjer). */
    white-space: pre-line;
    overflow-wrap: anywhere;
  }
  .hsw-bm-state {
    margin-top: 1.25rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
  .hsw-bm-state-booked,
  .hsw-bm-state-success {
    text-align: center;
    align-items: center;
    padding-top: 1rem;
  }
  .hsw-bm-state-booked strong,
  .hsw-bm-state-success strong {
    font-size: 1.05rem;
    margin-top: 0.25rem;
  }
  .hsw-bm-state-booked p,
  .hsw-bm-state-success p {
    font-size: 0.9rem;
    color: var(--yoga-text-muted, #737373);
    margin: 0;
    line-height: 1.5;
  }
  .hsw-bm-check {
    width: 48px;
    height: 48px;
    border-radius: 999px;
    background: rgba(22,163,74,0.12);
    color: #16a34a;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    font-weight: 700;
  }
  .hsw-bm-state-error strong {
    color: #b91c1c;
  }
  .hsw-bm-state-error p {
    color: var(--yoga-text-muted, #737373);
    font-size: 0.9rem;
    margin: 0;
  }
  .hsw-bm-price {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    padding: 0.75rem 1rem;
    background: rgba(0,0,0,0.03);
    border-radius: var(--yoga-radius, 6px);
  }
  .hsw-bm-price-label {
    font-size: 0.85rem;
    color: var(--yoga-text-muted, #737373);
  }
  .hsw-bm-price-amount {
    font-size: 1.1rem;
    font-weight: 700;
  }
  .hsw-bm-primary {
    display: inline-block;
    background: var(--yoga-primary, #FF6B35);
    color: #fff;
    border: 1px solid var(--yoga-primary, #FF6B35);
    border-radius: var(--yoga-radius, 6px);
    padding: 0.85rem 1.25rem;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
    transition: background .2s, opacity .2s;
    text-decoration: none;
    text-align: center;
    font-family: inherit;
  }
  .hsw-bm-primary:hover:not(:disabled) {
    background: var(--yoga-primary-900, #1A1A2E);
    border-color: var(--yoga-primary-900, #1A1A2E);
  }
  .hsw-bm-primary:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  .hsw-bm-secondary {
    background: transparent;
    color: var(--yoga-text, #0a0a0a);
    border: 1px solid var(--yoga-card-border, #d4d4d4);
    border-radius: var(--yoga-radius, 6px);
    padding: 0.7rem 1.25rem;
    font-size: 0.9rem;
    cursor: pointer;
    transition: background .2s;
    font-family: inherit;
  }
  .hsw-bm-secondary:hover:not(:disabled) {
    background: rgba(0,0,0,0.04);
  }
  .hsw-bm-secondary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .hsw-bm-cancel {
    background: transparent;
    color: #dc2626;
    border: 1px solid #fecaca;
    border-radius: var(--yoga-radius, 6px);
    padding: 0.85rem 1.25rem;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
    transition: background .2s, color .2s, border-color .2s;
    font-family: inherit;
  }
  .hsw-bm-cancel:hover:not(:disabled) {
    background: #dc2626;
    color: #fff;
    border-color: #dc2626;
  }
  .hsw-bm-cancel:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  .hsw-bm-member-covered {
    font-size: 0.85rem;
    color: #16a34a;
    background: rgba(22,163,74,0.08);
    border-radius: var(--yoga-radius, 6px);
    padding: 0.5rem 0.75rem;
    line-height: 1.4;
  }
  .hsw-bm-guests-add {
    font: inherit;
    font-size: 0.875rem;
    background: none;
    border: 0;
    padding: 0;
    color: inherit;
    opacity: 0.75;
    text-decoration: underline;
    cursor: pointer;
    text-align: left;
  }
  .hsw-bm-guests-add:hover { opacity: 1; }
  .hsw-bm-guests {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
    margin: 0.25rem 0 0.25rem;
  }
  .hsw-bm-guests-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
  }
  .hsw-bm-guests-label {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
  }
  .hsw-bm-guests-label > span:first-child {
    font-size: 0.95rem;
    font-weight: 600;
  }
  .hsw-bm-guests-unit {
    font-size: 0.8rem;
    color: var(--yoga-text-muted, #737373);
  }
  .hsw-bm-guests-stepper {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  .hsw-bm-guests-btn {
    width: 2.25rem;
    height: 2.25rem;
    border-radius: var(--yoga-radius, 6px);
    border: 1px solid var(--yoga-card-border, #d4d4d4);
    background: transparent;
    color: var(--yoga-text, #0a0a0a);
    font-size: 1.25rem;
    line-height: 1;
    cursor: pointer;
    font-family: inherit;
    transition: background .2s, opacity .2s;
  }
  .hsw-bm-guests-btn:hover:not(:disabled) {
    background: rgba(0,0,0,0.04);
  }
  .hsw-bm-guests-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  .hsw-bm-guests-count {
    min-width: 1.5rem;
    text-align: center;
    font-size: 1.05rem;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }
  .hsw-bm-guest-link {
    display: block;
    text-align: center;
    margin-top: 0.25rem;
    font-size: 0.85rem;
    font-weight: 500;
    color: var(--yoga-primary, #FF6B35);
    text-decoration: underline;
    cursor: pointer;
  }
  .hsw-bm-error {
    background: #fef2f2;
    border: 1px solid #fecaca;
    color: #b91c1c;
    padding: 0.7rem 0.85rem;
    border-radius: var(--yoga-radius, 6px);
    font-size: 0.85rem;
    line-height: 1.4;
  }
  .hsw-bm-guest {
    margin-top: 0.5rem;
    padding-top: 1rem;
    border-top: 1px solid var(--yoga-card-border, #e5e7eb);
  }
  .hsw-bm-guest-toggle {
    width: 100%;
    background: transparent;
    color: var(--yoga-text, #0a0a0a);
    border: 1px solid var(--yoga-card-border, #d4d4d4);
    border-radius: var(--yoga-radius, 6px);
    padding: 0.7rem 1.25rem;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    transition: background .2s;
    font-family: inherit;
  }
  .hsw-bm-guest-toggle:hover {
    background: rgba(0,0,0,0.04);
  }
  .hsw-bm-guest-form {
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
  }
  .hsw-bm-guest-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.85rem;
  }
  @media (max-width: 420px) {
    .hsw-bm-guest-row { grid-template-columns: 1fr; }
  }
  .hsw-bm-guest-field {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }
  .hsw-bm-guest-label {
    font-size: 0.82rem;
    font-weight: 600;
    color: var(--yoga-text, #0a0a0a);
  }
  .hsw-bm-guest-optional {
    font-weight: 400;
    color: var(--yoga-text-muted, #737373);
  }
  .hsw-bm-guest-input {
    width: 100%;
    box-sizing: border-box;
    background: var(--yoga-card-bg, #ffffff);
    color: var(--yoga-text, #0a0a0a);
    border: 1px solid var(--yoga-card-border, #d4d4d4);
    border-radius: var(--yoga-radius, 6px);
    padding: 0.7rem 0.85rem;
    font-size: 0.92rem;
    font-family: inherit;
    line-height: 1.4;
    transition: border-color .2s, box-shadow .2s;
    outline: none;
  }
  .hsw-bm-guest-input:focus {
    border-color: var(--yoga-primary, #FF6B35);
    box-shadow: 0 0 0 3px rgba(255,107,53,0.15);
  }
  .hsw-bm-guest-input:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
  .hsw-bm-guest-consent {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    font-size: 0.85rem;
    line-height: 1.4;
    color: var(--yoga-text, #0a0a0a);
    cursor: pointer;
  }
  .hsw-bm-guest-consent input {
    margin-top: 0.15rem;
    flex-shrink: 0;
  }
  .hsw-bm-guest-legal {
    color: var(--yoga-primary, #FF6B35);
    text-decoration: underline;
  }
  .hsw-bm-guest-intro,
  .hsw-bm-guest-hint {
    margin: 0;
    font-size: 0.85rem;
    line-height: 1.45;
    color: var(--yoga-text-muted, #737373);
  }
`
